// src/components/SearchOverlay.js
import React, { useContext, useState } from "react";
import { LanguageContext } from "../context/LanguageContext";
import { useNavigate } from "react-router-dom";
import { getPublicCategories } from "../services/categories.api";
import { getAllProducts } from "../services/products.api";
import { mapBackendProduct } from "../utils/mapProduct";
import { useEffect } from "react";
import { getHomepagePopularSearchOrder } from "../services/homepage.api";

function SearchOverlay({ isOpen, onClose = () => {} }) {
  const { lang } = useContext(LanguageContext);
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [categoryItems, setCategoryItems] = useState([]);
  
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggest, setLoadingSuggest] = useState(false);
  
  const [popularOrder, setPopularOrder] = useState([]);


  useEffect(() => {
  async function load() {
    try {
      const [cats, order] = await Promise.all([
        getPublicCategories(),
        getHomepagePopularSearchOrder(),
      ]);

      setPopularOrder(Array.isArray(order) ? order : []);

      const enriched = await Promise.all(
        cats.map(async (c) => {
          const products = await getAllProducts({ category: c.slug });

const mapped = products
  .map(mapBackendProduct)
  .sort(
    (a, b) => new Date(b.created_at) - new Date(a.created_at)
  );


          return {
            key: c.slug,
            labelEN: c.name_en,
            labelAR: c.name_ar,
            img: mapped[0]?.images?.[0] || "/images/placeholder-product.jpg",
          };
        })
      );

      setCategoryItems(enriched);
    } catch (e) {
      console.error(e);
    }
  }

  if (isOpen) load();
}, [isOpen]);


  useEffect(() => {
  if (!query.trim() || query.trim().length < 2) {
    setSuggestions([]);
    return;
  }

  const controller = new AbortController();
  const q = query.trim();

  const t = setTimeout(async () => {
    try {
      setLoadingSuggest(true);

      const products = await getAllProducts({ search: q });
      const mapped = products.map(mapBackendProduct);

      const set = new Set();

      mapped.forEach((p) => {
        // product names
        if (p.nameEN?.toLowerCase().includes(q.toLowerCase()))
          set.add(p.nameEN);
        if (p.nameAR?.includes(q)) set.add(p.nameAR);

        // public_id
        if (p.public_id?.toLowerCase().includes(q.toLowerCase()))
          set.add(p.public_id);

        // slug
        if (p.slug?.toLowerCase().includes(q.toLowerCase()))
          set.add(p.slug.replace(/-/g, " "));

        // categories
        (p.categories || []).forEach((c) => {
          if (c.toLowerCase().includes(q.toLowerCase()))
            set.add(c.replace(/-/g, " "));
        });

        // colors
        (p.colors || []).forEach((c) => {
          if (c.name?.toLowerCase().includes(q.toLowerCase()))
            set.add(c.name);
        });

        // sizes
        (p.sizes || []).forEach((s) => {
          const sizeStr = String(s.size);
          if (sizeStr.includes(q)) set.add(sizeStr);
        });

        // tags
        (p.tags || []).forEach((t) => {
          if (t.toLowerCase().includes(q.toLowerCase()))
            set.add(t.replace(/-/g, " "));
        });
      });

      setSuggestions(Array.from(set).slice(0, 3));
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingSuggest(false);
    }
  }, 250); // debounce

  return () => {
    clearTimeout(t);
    controller.abort();
  };
}, [query]);


  // -----------------------------
  // POPULAR SEARCHES (OLD + NEW)
  // -----------------------------
  const POPULAR_RAW = [
  ...categoryItems,
  { key: "new-in", labelEN: "New In", labelAR: "وصل حديثاً", customNew: true },
  { key: "sale", labelEN: "Sale", labelAR: "تخفيضات", customIcon: true },
  { key: "bestsellers", labelEN: "Bestsellers", labelAR: "الأكثر مبيعاً", customBest: true },
];

const POPULAR = (() => {
  if (!popularOrder?.length) return POPULAR_RAW;

  const idx = new Map(popularOrder.map((k, i) => [k, i]));

  return POPULAR_RAW
    .slice()
    .sort((a, b) => {
      const ai = idx.has(a.key) ? idx.get(a.key) : 999999;
      const bi = idx.has(b.key) ? idx.get(b.key) : 999999;
      return ai - bi;
    });
})();


const goToCategory = (key) => {
  onClose();
  navigate(`/products?category=${key}`, {
    state: { from: "home" },
  });
};

const submitSearch = () => {
  if (!query.trim()) return;

  onClose();
  navigate(`/products?search=${encodeURIComponent(query.trim())}`, {
    state: { from: "home" },
  });
};


  return (
    <div
      className={`
        fixed inset-0 z-[9999]
        transition-opacity duration-300
        ${isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}
      `}
    >
      {/* BACKDROP */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>

      {/* MAIN PANEL */}
      <div
        className={`
          absolute top-0 left-0 w-full h-full bg-white
          transform transition-transform duration-300
          ${isOpen ? "translate-y-0" : "-translate-y-full"}
          px-6 pt-8
        `}
        dir={lang === "AR" ? "rtl" : "ltr"}
      >
        {/* CLOSE BUTTON WITH LANGUAGE DIRECTION */}
        <button
          onClick={onClose}
          className={`
            absolute top-4 text-3xl text-black hover:opacity-70 transition
            ${lang === "EN" ? "right-4" : "left-4"}
          `}
        >
          ×
        </button>

        {/* TITLE */}
        <h2 className="text-xl font-semibold text-black mb-5 tracking-wide">
          {lang === "EN" ? "Search" : "بحث"}
        </h2>

        {/* SEARCH BAR */}
        <div className="relative w-full mb-8">
          <input
  type="text"
  value={query}
  onChange={(e) => setQuery(e.target.value)}
  onKeyDown={(e) => {
    if (e.key === "Enter") {
      submitSearch();
    }
  }}
  placeholder={lang === "EN" ? "Search products..." : "ابحث عن المنتجات..."}
  className="
    w-full border border-silver rounded-full
    py-3 pl-5 pr-12 text-sm focus:outline-none
    text-black placeholder-gray-400 shadow-sm
  "
/>

{suggestions.length > 0 && (
  <div className="absolute z-50 mt-2 w-full bg-white border border-[#E6D8D2] rounded-lg shadow-md overflow-hidden">
    {suggestions.map((s, i) => (
      <button
        key={i}
onClick={() => {
  onClose();
  navigate(`/products?search=${encodeURIComponent(s)}`, {
    state: { from: "home" },
  });
}}

        className="
          w-full text-left px-4 py-3 text-sm
          hover:bg-[#F7F3F1] transition
        "
      >
        🔍 {s}
      </button>
    ))}
  </div>
)}



          {/* SEARCH BUTTON */}
          <button
  onClick={submitSearch}
  className="
    absolute top-1/2 -translate-y-1/2 right-3
    bg-black w-9 h-9 rounded-full
    flex items-center justify-center shadow-md
    active:scale-95 transition
  "
>
  <img src="/icons/search-white.png" className="w-4 h-4" />
</button>

        </div>

        {/* POPULAR SEARCH TEXT */}
        <p className="text-sm font-semibold text-black mb-4 tracking-wide">
          {lang === "EN" ? "Popular Searches" : "عمليات البحث الشائعة"}
        </p>

        {/* POPULAR SEARCH ITEMS */}
        <div className="flex flex-wrap gap-6">
          {POPULAR.map((item, i) => (
            <button
              key={i}
              onClick={() => goToCategory(item.key)}
              className="flex flex-col items-center hover:opacity-80 transition"
            >
              {/* CIRCLE ICON / IMAGE */}
              <div
                className="
                  w-16 h-16 rounded-full overflow-hidden 
                  border-2 border-silver shadow-sm bg-white
                  flex items-center justify-center
                "
              >
                {/* REGULAR IMAGE */}
                {!item.customIcon && !item.customBest && !item.customNew && (
                  <img
                    src={item.img}
                    alt={item.labelEN}
                    className="w-full h-full object-cover"
                  />
                )}

                {/* 🔥 SALE BADGE ICON */}
                {item.customIcon && (
                  <div
                    className="
                      w-12 h-12 rounded-full 
                      bg-red-600 text-white text-xs font-bold
                      flex items-center justify-center
                      shadow-[0_0_10px_rgba(255,0,0,0.7)]
                      animate-pulse
                    "
                  >
                    SALE
                  </div>
                )}

                {/* ⭐ BESTSELLER BADGE ICON */}
                {item.customBest && (
                  <div
                    className="
                      w-12 h-12 rounded-full 
                      bg-yellow-400 text-black text-xs font-bold
                      flex items-center justify-center
                      shadow-[0_0_10px_rgba(255,200,0,0.7)]
                    "
                  >
                    ⭐ BEST
                  </div>
                )}
               {item.customNew && (
  <div
    className="
      w-12 h-12 rounded-full
      bg-black text-white
      text-xs font-bold
      flex items-center justify-center
      tracking-wide
      shadow-[0_0_10px_rgba(0,0,0,0.6)]
    "
  >
    NEW
  </div>
)}


              </div>

              {/* LABEL */}
              <span
                className="
                  mt-2 bg-roseNude text-black text-xs 
                  px-4 py-1 rounded-full whitespace-nowrap
                  font-medium shadow-sm
                "
              >
                {lang === "EN" ? item.labelEN : item.labelAR}
              </span>
            </button>
          ))}
        </div>
        <div className="mt-10 flex justify-center">
  <button
    onClick={() => {
      onClose();
      navigate("/all-products");
    }}
    className="
      w-full max-w-md
      py-3
      bg-black text-white
      rounded-full
      text-sm font-semibold tracking-wide
      shadow-md
      active:scale-95
      transition
    "
  >
    {lang === "EN" ? "EXPLORE ALL PRODUCTS" : "تصفح كل المنتجات"}
  </button>
</div>

      </div>
    </div>
  );
}

export default SearchOverlay;
